import {
    combinePath,
    getDataFieldsContent,
    getDataFieldsNode,
    getQueryXml,
} from '../../dataConnections/dataConnection.utils';
import { parseXmlNative } from '../../utility/xmlHelpers';
import { omitDiffgram } from './helpers/soap.helpers';
import {
    DOM_DOCUMENT_NODE,
    ownerDocument,
    xmlToString,
} from '../../xmlUtility';

const parseXML = parseXmlNative;
const $ = typeof window !== 'undefined' && window.jQuery;

/**
 * Retrieves the initial source for this data adapter
 * @returns - Promise for a DOMNode
 */
const getWebServiceSourceAsync = (template, definition) =>
    template.getTemplateFileAsync(definition.input.source).then(parseXML);

/**
 * Retrieves the DOM to use for submitting
 * if dataSource is specified, returns the dataSource's DOM,
 * or otherwise a DOM for the adapter's template file
 */
async function getDomAsync(dataSource, template, definition) {
    if (dataSource) {
        return dataSource.getDom();
    }

    return await getWebServiceSourceAsync(template, definition);
}

// DataSource, DomNode -> void
function setQueryResult(dataSource, node) {
    const dom = dataSource.getDom();
    const dataFieldsNode = getDataFieldsNode(dom);
    const toInsert = omitDiffgram(node);

    if (dataFieldsNode) {
        $(dataFieldsNode).empty().append($(toInsert).children());
    }
}

// (DataSourceCollection, WebServiceInputFragment) -> Array[DOMNode]
function getFragmentValues(dataSources, fragmentDefinition) {
    const dataSource = dataSources.getDom(fragmentDefinition.dataObject);

    const contextNode = dataSource.selectSingle(fragmentDefinition.replaceWith);

    if (!contextNode) {
        return [];
    }

    if (fragmentDefinition.filter) {
        return contextNode
            .selectNodes(fragmentDefinition.filter)
            .map((n) => n.getNode());
    }

    return Array.prototype.slice.call(contextNode.getNode().childNodes);
}

function getStringValue(node) {
    // TODO: import from a module
    if (node.nodeType === DOM_DOCUMENT_NODE) {
        return xmlToString(node);
    }

    return xmlToString(node);
}

function populateDestNodeWithNodes(destNode, fragmentValues) {
    var doc = ownerDocument(destNode),
        importedNodes = fragmentValues.map(function (node) {
            return doc.importNode(node, true);
        });

    $(destNode).append($(importedNodes));
}

function setWebServiceSubmitDestNode(destNode, fragmentValues, asString) {
    if (asString) {
        $(destNode).text(fragmentValues.map(getStringValue).join(''));
    } else {
        populateDestNodeWithNodes(destNode, fragmentValues);
    }
}

function prepareSubmitInput(dataSources, definition, dom, xpathEngine) {
    const fragments = definition.input.fragments;

    if (fragments) {
        fragments.forEach(function (fragment) {
            const fragmentValues = getFragmentValues(dataSources, fragment);
            const destNode = xpathEngine
                .evaluateXPath(fragment.match, { context: dom })
                .first();

            if (destNode) {
                setWebServiceSubmitDestNode(
                    destNode,
                    fragmentValues,
                    fragment.sendAsString,
                );
            }
        });
    }

    return dom;
}

function getDomain(webUrl) {
    if (!webUrl) {
        return null;
    }

    var urlParser = document.createElement('a');
    urlParser.href = webUrl;

    return urlParser.protocol + '//' + urlParser.host;
}

const getRequestBody = (dom, isQuery) =>
    isQuery ? getQueryXml(dom) : getDataFieldsContent(dom);

const performSoapQuery = async (
    dataSources,
    dataSource,
    template,
    definition,
    xpathEngine,
    api,
    url,
) => {
    // [2021-04-01] Removed call to executeDirectAsync()
    const dom = await getDomAsync(dataSource, template, definition);

    if (!dom) {
        throw new Error(
            'Unable to prepare the request XML for data source ' +
                definition.name,
        );
    }

    const { methodName, soapAction } = definition;

    const domCopy = dom.cloneNode(true);

    prepareSubmitInput(dataSources, definition, domCopy, xpathEngine);

    const body = getRequestBody(domCopy, !!dataSource);
    const overrideAction = false;
    const spHostUrl = getDomain(window.getHostWebUrl());
    const useCookie = !!url && spHostUrl === getDomain(url);

    return api.querySoapAsync(
        url,
        methodName,
        soapAction,
        xmlToString(body),
        overrideAction,
        useCookie,
    );
};

const applyQueryResult = (data, dataSource) => {
    const dataDom = $.parseXML(data.resultBody);

    if (dataSource) {
        setQueryResult(dataSource, dataDom);
    }

    return dataDom;
};

/** Creates a new SOAP adapter
 * @param {definition} definition - The data source definition object
 * @param {api} api - an instance of the FormsViewer web API
 * @Returns {soapAdapter}
 */
function soapAdapter(definition, template, api, xpathEngine, dataSources) {
    let url = combinePath(template.getBasePath(), definition.serviceUrl);

    // 2022-03-04 - Removed executeDirectAsync

    async function executeAsync(dataSource) {
        const data = await performSoapQuery(
            dataSources,
            dataSource,
            template,
            definition,
            xpathEngine,
            api,
            url,
        );

        if (data.success) {
            return applyQueryResult(data, dataSource);
        }

        throw new Error(data.message);
    }

    async function initAsync(dataSource) {
        if (dataSource) {
            const dom = await getWebServiceSourceAsync(template, definition);

            dataSource.setDom(dom);
        }
    }

    function setUrl(serviceUrl) {
        url = serviceUrl;
    }

    function getUrl() {
        return url;
    }

    return {
        /** Executes this data connection and updates the specified data
         *  source with the new data
         * @param {dataSource} dataSource - The data source
         * @returns {Promise}
         */
        executeAsync,
        initAsync,
        setUrl,
        getUrl,
    };
}

export default soapAdapter;
